import { useState, useEffect, useRef } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Copy-to-clipboard button with inline "Copied" feedback.
 *
 * Used by Audit Logs (trace ids, prompts) and the Phase 8 finding
 * decision-basis panel (agent input/output blocks).
 */
export function CopyButton({ value, label = "Copy", className, showLabel = false, ...props }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const onCopy = (event) => {
    event?.stopPropagation?.();
    if (!value) return;
    try {
      navigator?.clipboard?.writeText?.(String(value))?.catch?.(() => {});
    } catch {
      /* clipboard unavailable */
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      title={label}
      aria-label={label}
      {...props}
      className={cn(
        "inline-flex h-6 items-center gap-1 cursor-pointer rounded-md px-1.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50",
        copied && "text-emerald-600",
        className,
      )}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {showLabel && <span>{copied ? "Copied" : label}</span>}
    </button>
  );
}

export default CopyButton;
